import { PlusOutlined, DeleteOutlined, SearchOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import {
    Button, Col, ConfigProvider, DatePicker, FloatButton, Input, Row, Select, Space, Spin, Switch, Table, Tooltip,
} from "antd";
import { Footer } from "antd/es/layout/layout";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { rightsService } from "../../../services/rightsService";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { frenchDate, frenchDateSimple } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { StatCard } from "../../../components/statistics/StatCard";
import { COLORS } from "../../../components/ui/colors";
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from "../../stats/sinistre/etatsSinistresFunctions";

const { RangePicker } = DatePicker;
const dateFormat = "DD/MM/YYYY";
const SS_KEY = "filters:propositions:v2";

// Format de nombre compatible jsPDF (séparateur = espace ASCII ; l'espace fine de fr-FR → "/").
const pdfNum = (v) => new Intl.NumberFormat("en-DE").format(Math.floor(v || 0)).replace(/\./g, " ");
const num0 = (v) => new Intl.NumberFormat("en-DE", { maximumFractionDigits: 0 }).format(v || 0);

/**
 * Propositions V2 : filtres persistants (sessionStorage), recherche locale, export PDF/Excel de la
 * liste filtrée (entête/pied habituels), KPI et mise en corbeille (HCO092) repris de la v1.
 */
const PropositionsV2 = () => {
    // Restauration paresseuse des filtres persistés.
    const SAVED = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "null") || {}; }
        catch (e) { return {}; }
    }, []);

    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [loadingContrats, setLoadingContrats] = useState(true);
    const [membres, setMembres] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [count, setCount] = useState("");
    const [search, setSearch] = useState("");

    const [selectedDate, setSelectedDate] = useState(SAVED.selectedDate || null);
    const [selectedCompagnies, setSelectedCompagnies] = useState(SAVED.selectedCompagnies || []);
    const [bulkCorbeilleLoading, setBulkCorbeilleLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [pagination, setPagination] = useState({
        current: SAVED.current || 1, pageSize: SAVED.pageSize || 10, showSizeChanger: true,
    });

    const canCorbeille = rightsService("HCO092");
    const navigate = useNavigate();

    // Persistance des filtres.
    useEffect(() => {
        sessionStorage.setItem(SS_KEY, JSON.stringify({
            selectedDate, selectedCompagnies,
            current: pagination.current, pageSize: pagination.pageSize,
        }));
    }, [selectedDate, selectedCompagnies, pagination.current, pagination.pageSize]);

    const mapContrat = (contrat, key) => ({
        key: contrat.codeId,
        codeId: contrat.codeId,
        id: key,
        numeroPolice: contrat.numeroPoliceM ? contrat.numeroPoliceM : contrat.numeroPoliceP,
        souscripteur: contrat?.prenom ? `${contrat?.prenom} ${contrat?.nom}` : `${contrat?.nom || ""}`,
        organisme: contrat?.raisonSociale,
        producteur: contrat?.producteur || "",
        dateEffet: contrat?.dateEffet ? `${frenchDate(contrat?.dateEffet)}` : "",
        dateEcheance: contrat?.dateEcheance ? `${frenchDateSimple(contrat?.dateEcheance)} ` : "",
        primeTotaleNum: contrat?.primeTotale || 0,
        primeNetteNum: contrat?.primeNette || 0,
        accessoiresNum: contrat?.accessoires || 0,
        taxesNum: (contrat?.primeTotale || 0) * 0.06 / 1.06,
        primeTotale: num0(contrat?.primeTotale),
        taxes: num0((contrat?.primeTotale || 0) * 0.06 / 1.06),
        primeNette: num0(contrat?.primeNette),
        accessoires: num0(contrat?.accessoires),
        data: contrat,
    });

    const buildParams = (page, size) => {
        const queryParams = { page: page - 1, size };
        if (selectedDate && selectedDate[0] && selectedDate[1]) {
            queryParams.startDate = selectedDate[0];
            queryParams.endDate = selectedDate[1];
        }
        if (selectedCompagnies && selectedCompagnies.length) {
            queryParams.compagnie = selectedCompagnies.join(",");
        }
        return queryParams;
    };

    const fetchData = async (page = 1, size = 10, opts = {}) => {
        const date = opts.date !== undefined ? opts.date : selectedDate;
        const comps = opts.compagnies !== undefined ? opts.compagnies : selectedCompagnies;
        setLoadingContrats(true);
        try {
            const queryParams = { page: page - 1, size };
            if (date && date[0] && date[1]) { queryParams.startDate = date[0]; queryParams.endDate = date[1]; }
            if (comps && comps.length) { queryParams.compagnie = comps.join(","); }
            const response = await axiosInstance.get("/api/contrat/proposition", { params: queryParams });
            const { content, totalElements } = response.data;
            const uniqueContrats = Array.from(
                new Map((content || []).map((c) => [c.codeId, c])).values()
            );
            setData(uniqueContrats.map(mapContrat));
            setPagination((prev) => ({ ...prev, current: page, pageSize: size, total: totalElements }));
            setTotal(totalElements);
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoadingContrats(false);
        }
    };

    const handleChange = (pag) => fetchData(pag.current, pag.pageSize);

    const handleDateChange = (dates, dateStrings) => {
        const [sDate, eDate] = dateStrings || [];
        if (!sDate || !eDate) { setSelectedDate(null); fetchData(1, pagination.pageSize, { date: null }); return; }
        const [sd, sm, sy] = sDate.split("/");
        const [ed, em, ey] = eDate.split("/");
        const newDate = [`${sy}-${sm}-${sd}`, `${ey}-${em}-${ed}`];
        setSelectedDate(newDate);
        fetchData(1, pagination.pageSize, { date: newDate });
    };

    const filteringOnCompanies = (companies) => {
        setSelectedCompagnies(companies);
        fetchData(1, pagination.pageSize, { compagnies: companies });
    };

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
        axiosInstance.get(`/api/organisme/assurance`).then((res) => {
            const membs = (res.data || []).map((memb, index) => ({ key: index, text: memb.raisonSociale, value: memb.code }));
            [["DTRF", "DTRF"], ["DTRF-LOME", "LOME"], ["DTRF-KPALIME", "KPALIME"], ["DTRF-ATAKPAME", "ATAKPAME"],
            ["DTRF-SOKODE", "SOKODE"], ["DTRF-KARA", "KARA"], ["DTRF-DAPAONG", "DAPAONG"], ["STCA", "STCA"]]
                .forEach(([text, value]) => membs.push({ key: value, text, value }));
            setMembres(membs);
        }).catch((err) => console.log(err));
        axiosInstance.get(`/api/contrat/count`).then((res) => setCount(res.data)).catch((err) => console.log(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBulkCorbeille = () => {
        if (!selectedRowKeys || selectedRowKeys.length === 0) return;
        setBulkCorbeilleLoading(true);
        axiosInstance.put(`/api/contrat/proposition/corbeille`, selectedRowKeys)
            .then(() => {
                toast.success(`${selectedRowKeys.length} proposition(s) déplacée(s) dans la corbeille.`);
                setSelectedRowKeys([]); setSelecting(false);
                fetchData(pagination.current, pagination.pageSize);
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors de la mise en corbeille"))
            .finally(() => setBulkCorbeilleLoading(false));
    };

    // Recherche locale (sur la page chargée) : souscripteur / n° devis / organisme / auteur.
    const displayed = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((r) => `${r.numeroPolice || ""} ${r.souscripteur || ""} ${r.organisme || ""} ${r.producteur || ""}`.toLowerCase().includes(q));
    }, [data, search]);

    // Récupère TOUTES les lignes filtrées (période + compagnies) pour l'export.
    const fetchAllForExport = async () => {
        const params = buildParams(1, 100000);
        const res = await axiosInstance.get("/api/contrat/proposition", { params });
        const content = Array.from(new Map((res.data?.content || []).map((c) => [c.codeId, c])).values());
        return content.map(mapContrat);
    };

    const fileStamp = () => new Date().toISOString().slice(0, 10);

    const exportExcel = async () => {
        setExporting(true);
        try {
            const rows = await fetchAllForExport();
            const aoa = rows.map((r) => ({
                "N° devis": r.numeroPolice || "", "Souscripteur": r.souscripteur || "", "Organisme": r.organisme || "",
                "Proposé par": r.producteur || "",
                "Date effet": r.dateEffet || "", "Date échéance": (r.dateEcheance || "").trim(),
                "Prime nette": Math.trunc(r.primeNetteNum), "Accessoires": Math.trunc(r.accessoiresNum),
                "Taxes": Math.trunc(r.taxesNum), "Prime TTC": Math.trunc(r.primeTotaleNum),
            }));
            const ws = XLSX.utils.json_to_sheet(aoa);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Propositions");
            XLSX.writeFile(wb, `propositions-${fileStamp()}.xlsx`);
        } catch (e) { toast.error("Erreur lors de l'export Excel"); }
        finally { setExporting(false); }
    };

    const exportPDF = async () => {
        setExporting(true);
        try {
            const rows = await fetchAllForExport();
            const doc = new jsPDF({ orientation: "landscape" });
            let enteteData = null, logos = [];
            try { [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]); } catch (e) { /* optionnels */ }
            const formattedNow = new Date().toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            });
            autoTable(doc, {
                startY: 34, margin: { top: 32, bottom: 20, left: 14, right: 14 },
                head: [["N° devis", "Souscripteur", "Organisme", "Proposé par", "Date effet", "Date échéance", "Prime nette", "Access.", "Taxes", "Prime TTC"]],
                body: rows.map((r) => [
                    r.numeroPolice || "", r.souscripteur || "", r.organisme || "", r.producteur || "", r.dateEffet || "", (r.dateEcheance || "").trim(),
                    pdfNum(r.primeNetteNum), pdfNum(r.accessoiresNum), pdfNum(r.taxesNum), pdfNum(r.primeTotaleNum),
                ]),
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: NAVY, fontSize: 7 },
                didDrawPage: () => drawEtatHeaderFooter(doc, {
                    title: `PROPOSITIONS${selectedDate ? ` — ${dayjs(selectedDate[0]).format("DD/MM/YYYY")} au ${dayjs(selectedDate[1]).format("DD/MM/YYYY")}` : ""}`,
                    subtitle: `${rows.length} proposition(s)`, enteteData, logos, formattedNow,
                }),
            });
            finalizeEtatPdf(doc);
            doc.save(`propositions-${fileStamp()}.pdf`);
        } catch (e) { toast.error("Erreur lors de l'export PDF"); }
        finally { setExporting(false); }
    };

    const columns = [
        { title: "N° devis", dataIndex: "numeroPolice", key: "1" },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "2" },
        { title: "Organisme", dataIndex: "organisme", key: "3" },
        { title: "Proposé par", dataIndex: "producteur", key: "producteur" },
        { title: "Date Effet", dataIndex: "dateEffet", key: "4" },
        { title: "Date Echeance", dataIndex: "dateEcheance", key: "5" },
        { title: "Prime nette", dataIndex: "primeNette", key: "primeNette", align: "right" },
        { title: "Accessoires", dataIndex: "accessoires", key: "accessoires", align: "right" },
        { title: "Taxes", dataIndex: "taxes", key: "taxes", align: "right" },
        { title: "Prime TTC", dataIndex: "primeTotale", key: "primeTotale", align: "right" },
        Table.EXPAND_COLUMN,
        {
            title: "Actions", dataIndex: "actions", key: "7",
            render: (_, record) => (
                <Tooltip title="Détails">
                    <button type="button" className="btn btn-primary btn-icon rounded-pill"
                        onClick={() => navigate(`/details-contrat/${record.codeId}`)}>
                        <i className="bx bx-dots-vertical-rounded"></i>
                    </button>
                </Tooltip>
            ),
        },
    ];

    const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };
    const isPoolSiege = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"
        && (LOGGED_USER?.agence?.typeAgence === "SIEGE" || LOGGED_USER?.agence == null);

    // flex "1 1 200px" : cards de largeur homogène, qui grandissent/rétrécissent et se replient
    // proprement (l'espacement est géré par le gutter du Row, pas par des marges manuelles).
    const kpi = (title, subtitle, value, icon) => (
        <Col flex="1 1 200px" style={{ minWidth: 200 }}>
            <StatCard title={title} subtitle={subtitle} value={value} icon={icon} iconBg={COLORS.secondary} gradient={COLORS.secondary} />
        </Col>
    );

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <Row gutter={[16, 16]} style={{ marginBottom: "15px" }}>
                                {kpi("Contrats", "Total contrats", displayNumbers(total || 0), "bx bx-file")}
                                {kpi("Contrats validés", "Actifs / validés", `${count?.activeCount ? displayNumbers(count?.activeCount) : 0} / ${count?.valideCount ? displayNumbers(count?.valideCount) : 0}`, "bx bx-check-circle")}
                                {kpi("Contrats annulés", "Contrats annulés", displayNumbers(count?.annuleCount || 0), "bx bx-x-circle")}
                                {kpi("En attente", "En proposition", displayNumbers(count?.enPropositionCount || 0), "bx bx-time-five")}
                                {kpi("Corbeille", "En corbeille", displayNumbers(count?.corbeilleCount || 0), "bx bx-trash")}
                            </Row>

                            <div className="row">
                                <div className="col-sm-6" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                                    {(rightsService("HCO022") || rightsService("HCO005") || rightsService("HCO019")) && (
                                        <Button type="primary" icon={<PlusOutlined />} style={{ backgroundColor: COLORS.secondary }}
                                            onClick={() => navigate("/ajouter-contrat-new")}>Nouveau contrat</Button>
                                    )}
                                    <Button icon={<FileExcelOutlined />} loading={exporting} onClick={exportExcel}
                                        style={{ borderColor: "#059669", color: "#059669" }}>Excel</Button>
                                    <Button icon={<FilePdfOutlined />} loading={exporting} onClick={exportPDF}
                                        style={{ borderColor: "#dc2626", color: "#dc2626" }}>PDF</Button>
                                </div>
                            </div>

                            <div className="card">
                                <div className="container">
                                    <div className="card">
                                        <div className="card-body">
                                            <Row gutter={[12, 12]} align="middle">
                                                {canCorbeille && (
                                                    <Col xs={24} md={5}>
                                                        <Space align="center">Mode sélection : <Switch checked={selecting} onChange={setSelecting} /></Space>
                                                    </Col>
                                                )}
                                                <Col xs={24} md={6}>
                                                    <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                        placeholder="Rechercher (devis, souscripteur, organisme, auteur)…"
                                                        value={search} onChange={(e) => setSearch(e.target.value)} />
                                                </Col>
                                                <Col xs={24} md={7}>
                                                    <ConfigProvider locale={frFR}>
                                                        <RangePicker style={{ width: "100%" }} format={dateFormat} onChange={handleDateChange}
                                                            value={selectedDate ? [dayjs(selectedDate[0]), dayjs(selectedDate[1])] : null}
                                                            placeholder={["Début", "Fin"]} />
                                                    </ConfigProvider>
                                                </Col>
                                                {isPoolSiege && (
                                                    <Col xs={24} md={6}>
                                                        <Select mode="multiple" allowClear style={{ width: "100%" }} placeholder="Filtre par compagnie"
                                                            value={selectedCompagnies} onChange={filteringOnCompanies} optionFilterProp="label"
                                                            options={membres.map((memb) => ({ label: memb.text, value: memb.value, key: memb.value }))} />
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    </div>
                                </div>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingContrats}>
                                        <Table
                                            rowSelection={selecting ? rowSelection : null}
                                            columns={columns}
                                            rowKey="codeId"
                                            dataSource={displayed}
                                            onChange={handleChange}
                                            scroll={{ x: "max-content" }}
                                            pagination={{
                                                ...pagination, showSizeChanger: true,
                                                pageSizeOptions: ["10", "20", "50", "100"],
                                                showTotal: (t) => `${t} proposition(s)`,
                                            }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </div>
                        </div>

                        {canCorbeille && selecting && selectedRowKeys.length > 0 && (
                            <div style={{
                                position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, background: "#fff",
                                boxShadow: "0 -2px 10px rgba(0,0,0,0.15)", padding: "12px 24px",
                                display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16,
                            }}>
                                <span style={{ fontWeight: 600 }}>{selectedRowKeys.length} proposition(s) sélectionnée(s)</span>
                                <Button onClick={() => setSelectedRowKeys([])}>Annuler la sélection</Button>
                                <Button type="primary" danger icon={<DeleteOutlined />} loading={bulkCorbeilleLoading} onClick={handleBulkCorbeille}>
                                    Mettre dans la corbeille
                                </Button>
                            </div>
                        )}

                        {canCorbeille && (
                            <FloatButton icon={<DeleteOutlined />} type="primary" tooltip="Corbeille des propositions"
                                style={{ right: 24, bottom: 24 }} onClick={() => navigate("/corbeille-propositions")} />
                        )}

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default PropositionsV2;
